/**
 * Committing an event: write the record, then let the world react.
 *
 * The events layer is a plain ledger (`sim/memory/events.ts`). This is where
 * an event has consequences in other stores:
 *
 *  - Every NPC who saw it, or had it done to them, forms an impression under
 *    the §11 permanence rules. A direct witness believes exactly what
 *    happened; distortion is a rumor-graph concern (V1, §20).
 *  - Violence writes a grievance for whoever it was done to (§13). The
 *    ladder is not active in V0; the object is stored (§20).
 */

import { mintGrievanceId } from './ids.js';
import { type EventDraft, isViolent, recordEvent } from '../engine/memory/events.js';
import { believedFrom, classifyPermanence, formImpression, tagsForEvent } from '../engine/memory/impressions.js';
import { TUNING } from './tuning.js';
import type { Actor, Event, Grievance, Impression, NpcId, WorldState, WorldTime } from './types/index.js';

export interface CommittedEvent {
  readonly state: WorldState;
  readonly event: Event;
  readonly impressions: readonly Impression[];
}

/** Events that touch a person, as opposed to merely involving them (§11 bonds). */
const TOUCHING: ReadonlySet<string> = new Set([
  'fistfight',
  'shakedown',
  'robbery',
  'hit',
  'gunfight',
  'interrogation',
  'death',
  'arrest',
  'departure',
  'lie_discovered',
  'secret_exposed',
  'appointment_missed',
  'obligation_lapsed',
]);

/** Everyone bonded to the actor or the target of a touching event. */
export function touchedBy(state: WorldState, draft: EventDraft): NpcId[] {
  if (!TOUCHING.has(draft.type)) return [];
  const people: NpcId[] = [];
  if (draft.actor !== 'player') people.push(draft.actor);
  if (draft.target?.kind === 'npc') people.push(draft.target.id);
  const out = new Set<NpcId>();
  for (const npc of Object.values(state.npcs)) {
    if (people.some((id) => id !== npc.id && npc.bonds.includes(id))) out.add(npc.id);
  }
  return [...out].sort();
}

/** NPCs who perceived the event first-hand and should remember it. */
function perceivers(state: WorldState, event: Event): NpcId[] {
  const ids = new Set<NpcId>();
  for (const w of event.witnesses) {
    if (w !== 'player' && state.npcs[w]) ids.add(w);
  }
  // Being on the receiving end counts as seeing it — except a conversation,
  // where the counterpart's record is the events layer, not a memory of
  // having been talked to.
  if (event.type !== 'conversation' && event.target?.kind === 'npc' && state.npcs[event.target.id]) {
    ids.add(event.target.id);
  }
  if (event.actor !== 'player') ids.delete(event.actor); // he knows what he did
  return [...ids].sort();
}

export function commitEvent(state: WorldState, draft: EventDraft, at: WorldTime = state.time): CommittedEvent {
  const touches = touchedBy(state, draft);
  const recorded = recordEvent(state, touches.length > 0 ? { ...draft, touches } : draft, at);
  let next = recorded.state;
  const { event } = recorded;
  const impressions: Impression[] = [];

  const firstHand = perceivers(next, event);
  for (const npcId of firstHand) {
    const npc = next.npcs[npcId];
    if (!npc || npc.status === 'dead') continue;
    const trigger = classifyPermanence(npc, event);
    const formed = formImpression(next, npcId, believedFrom(event), {
      origin: event.id,
      source: { kind: 'witnessed' },
      tags: tagsForEvent(event),
      ...(trigger === null ? {} : { trigger }),
    }, at);
    next = formed.state;
    impressions.push(formed.impression);
  }

  // Their people find out (§11): bonded NPCs who weren't there hear it
  // from the person it happened to. Permanent by the categorical rule.
  const teller: Actor = event.target?.kind === 'npc' ? event.target.id : event.actor;
  for (const npcId of touches) {
    if (firstHand.includes(npcId) || npcId === event.actor) continue;
    const npc = next.npcs[npcId];
    if (!npc || npc.status === 'dead') continue;
    const trigger = classifyPermanence(npc, event);
    const formed = formImpression(next, npcId, believedFrom(event), {
      origin: event.id,
      source: { kind: 'told', by: teller },
      tags: [...tagsForEvent(event), 'bond'],
      ...(trigger === null ? {} : { trigger }),
    }, at);
    next = formed.state;
    impressions.push(formed.impression);
  }

  if (isViolent(event.type) && event.target?.kind === 'npc' && event.target.id !== event.actor) {
    next = addGrievance(next, event.target.id, event.actor, event);
  }
  if (isViolent(event.type) && event.target?.kind === 'player' && event.actor !== 'player') {
    // Escalation is symmetric (§13): what he does to you is yours to hold.
    next = addGrievance(next, 'player', event.actor, event);
  }

  return { state: next, event, impressions };
}

function addGrievance(state: WorldState, holder: Actor, against: Actor, cause: Event): WorldState {
  const existing = Object.values(state.grievances).find(
    (g) => g.polarity === 'grievance' && g.holder === holder && g.against === against && g.status !== 'settled',
  );
  if (existing) {
    const bumped: Grievance = {
      ...existing,
      magnitude: existing.magnitude + TUNING.grievance.perViolentEvent,
    };
    return { ...state, grievances: { ...state.grievances, [bumped.id]: bumped } };
  }
  const minted = mintGrievanceId(state);
  const grievance: Grievance = {
    id: minted.id,
    holder,
    against,
    cause: cause.id,
    magnitude: TUNING.grievance.perViolentEvent,
    status: 'dormant',
    createdOn: cause.at,
    lastActedOn: null,
    polarity: 'grievance',
    rung: null,
  };
  return { ...minted.state, grievances: { ...minted.state.grievances, [grievance.id]: grievance } };
}
